import React from 'react';

import type { ComponentType } from '~/config/types';
import type { ConfiguredChild } from '~/hooks/use-config';
import { isComponent } from '~/config/utils';
import { Element, ElementWithSlot } from './element';

// ComponentRegistry is made mainly to avoid circular dependencies with components,
// since all components import utils.tsx to use renderChild.
class ComponentRegistry {
  private map: Map<ComponentType, React.FC>;

  constructor() {
    this.map = new Map<ComponentType, React.FC>();
  }

  add = (componentType: ComponentType, component: React.FC) => {
    this.map.set(componentType, component);
  };

  get = (componentType: ComponentType): React.FC | undefined => {
    return this.map.get(componentType);
  };
}

export const componentRegistry = new ComponentRegistry();

export function renderChild(child: ConfiguredChild, index?: number) {
  const indexStr = index !== undefined ? `-${index}` : '';

  if (isComponent(child)) {
    const children = Array.isArray(child.children)
      ? child.children.map((c, i) => {
          return renderChild(c as ConfiguredChild, i);
        })
      : child.children;

    const componentProps = { ...child.componentProps, children };
    const Component = componentRegistry.get(child.component);

    if (!Component) {
      throw new Error(`Component not found: ${child.component}. Make sure it is registered.`);
    }

    return <Component key={`${child.component}${indexStr}`} {...componentProps} />;
  }

  const { children } = child;
  const elementProps = {
    ...child.elementProps,
    ...(children && {
      children: Array.isArray(children)
        ? children.map((child, index) => {
            return renderChild(child, index);
          })
        : children,
    }),
  };

  return child.elementProps.slot ? (
    <ElementWithSlot key={`${child.elementProps.tag}${indexStr}`} {...elementProps} />
  ) : (
    <Element key={`${child.elementProps.tag}${indexStr}`} {...elementProps} />
  );
}

export function componentSlots<N extends string, S extends Record<string, string>>(
  componentName: N,
  slots: Readonly<S>
) {
  return {
    _ROOT: uncapitalize(componentName) as Uncapitalize<N>,
    ...(Object.fromEntries(
      Object.entries(slots).map(([key, value]) => [key, `${uncapitalize(componentName)}:${value}`])
    ) as {
      [K in keyof S]: `${Uncapitalize<N>}:${S[K]}`;
    }),
  } as const;

  function uncapitalize(str: string) {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }
}

export type DefineSlots<T extends Record<string, string>, D extends Record<T[Exclude<keyof T, '_ROOT'>], unknown>> = D;
